import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaBars, 
  FaTimes, 
  FaCode, 
  FaPhone, 
  FaEnvelope, 
  FaChevronDown, 
  FaMobile, 
  FaPalette, 
  FaShoppingCart, 
  FaCloud, 
  FaCogs, 
  FaBullhorn, 
  FaLaptopCode,
  FaRocket,
  FaGem,
  FaStar,
  FaWhatsapp,
  FaLinkedin,
  FaTwitter,
  FaUsers,
  FaHandshake,
  FaReact,
  FaNodeJs,
  FaDatabase,
  FaMobileAlt
} from 'react-icons/fa';
import { SiTypescript, SiPython, SiDocker } from 'react-icons/si';
import './Header.scss';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isMobileServicesDropdownOpen, setIsMobileServicesDropdownOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isMobileAboutDropdownOpen, setIsMobileAboutDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const mobileDropdownRef = useRef<HTMLLIElement>(null);
  const aboutDropdownRef = useRef<HTMLLIElement>(null);
  const mobileAboutDropdownRef = useRef<HTMLLIElement>(null);
  const location = useLocation();

  const navigation = [
    { 
      name: 'About', 
      path: '/about',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Who We Are', path: '/who-we-are', icon: FaUsers },
        { name: 'How We Work', path: '/development-process', icon: FaHandshake },
        { name: 'Reviews', path: '/development-reviews', icon: FaStar },
        { name: 'Portfolio', path: '/portfolio', icon: FaGem },
        { name: 'Pricing', path: '/pricing', icon: FaRocket }
      ]
    },
    { 
      name: 'Services', 
      path: '/services',
      hasDropdown: true,
      dropdownItems: [
        
       
        { name: 'Web Application', path: '/services/web-application-development', icon: FaLaptopCode },
        { name: 'Mobile Application', path: '/services/mobile-app-development', icon: FaMobile },
        { name: 'Web Development', path: '/services/web-development', icon: FaLaptopCode },
        { name: 'E-commerce Solutions', path: '/services/ecommerce-development', icon: FaShoppingCart },
        { name: 'Cloud Solutions', path: '/services/cloud-solutions', icon: FaCloud },
        { name: 'DevOps', path: '/services/devops', icon: FaCogs },
        { name: 'UI/UX Design', path: '/services/ui-ux-design', icon: FaPalette },
        { name: 'Digital Marketing', path: '/services/digital-marketing', icon: FaBullhorn }
      ]
    },
    { name: 'Demo', path: '/portfolio' },
    { name: 'Blogs', path: '/blogs' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleClickOutside = (event: MouseEvent) => {
      // Handle desktop services dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsServicesDropdownOpen(false);
      }
      // Handle mobile services dropdown
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node)) {
        setIsMobileServicesDropdownOpen(false);
      }
      // Handle desktop about dropdown
      if (aboutDropdownRef.current && !aboutDropdownRef.current.contains(event.target as Node)) {
        setIsAboutDropdownOpen(false);
      }
      // Handle mobile about dropdown
      if (mobileAboutDropdownRef.current && !mobileAboutDropdownRef.current.contains(event.target as Node)) {
        setIsMobileAboutDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Ensure body scrolling is maintained (lightweight approach)
  useEffect(() => {
    // Only intervene if there are actual scroll issues
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = 'auto';
    }
  }, [isMenuOpen]);

  // Debug: Log dropdown states
  useEffect(() => {
    console.log('📊 Dropdown States:', {
      mobileServices: isMobileServicesDropdownOpen,
      mobileAbout: isMobileAboutDropdownOpen,
      menuOpen: isMenuOpen
    });
  }, [isMobileServicesDropdownOpen, isMobileAboutDropdownOpen, isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Close all dropdowns when toggling main menu
    if (isMenuOpen) {
      setIsServicesDropdownOpen(false);
      setIsMobileServicesDropdownOpen(false);
      setIsAboutDropdownOpen(false);
      setIsMobileAboutDropdownOpen(false);
    } else {
      // Also close dropdowns when opening menu (ensure clean state)
      setIsMobileServicesDropdownOpen(false);
      setIsMobileAboutDropdownOpen(false);
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsServicesDropdownOpen(false);
    setIsMobileServicesDropdownOpen(false);
    setIsAboutDropdownOpen(false);
    setIsMobileAboutDropdownOpen(false);
  };

  const toggleServicesDropdown = () => {
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
    setIsAboutDropdownOpen(false); // Close other dropdown
  };

  const toggleAboutDropdown = () => {
    setIsAboutDropdownOpen(!isAboutDropdownOpen);
    setIsServicesDropdownOpen(false); // Close other dropdown
  };

  const toggleMobileServicesDropdown = () => {
    const newState = !isMobileServicesDropdownOpen;
    console.log('=== MOBILE SERVICES TOGGLE ===');
    console.log('Current state:', isMobileServicesDropdownOpen);
    console.log('New state:', newState);
    setIsMobileServicesDropdownOpen(newState);
    setIsMobileAboutDropdownOpen(false);
  };

  const toggleMobileAboutDropdown = () => {
    const newState = !isMobileAboutDropdownOpen;
    console.log('=== MOBILE ABOUT TOGGLE ===');
    console.log('Current state:', isMobileAboutDropdownOpen);
    console.log('New state:', newState);
    setIsMobileAboutDropdownOpen(newState);
    setIsMobileServicesDropdownOpen(false);
  };

  return (
    <>
      {/* Top Contact Bar */}
      <div className="top-bar">
        <div className="top-bar__container">
          <div className="top-bar__content">
            <div className="top-bar__contact-info">
              <span className="top-bar__contact-item">
                <i className="far fa-envelope"></i> hello@appdeveloperpro.com
              </span>
              <span className="top-bar__contact-item">
                <i className="fas fa-map-marker-alt"></i> 827, Kachpura, Kundol, Agra, Uttar Pradesh 283111
              </span>
              <span className="top-bar__contact-item">
                <i className="fas fa-phone"></i> +918826360188
              </span>
              
            </div>
            <div className="top-bar__social-links">
              <a href="https://www.facebook.com/appdeveloperpro" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-facebook-f"></i>
              </a>
              <a href="https://www.linkedin.com/company/app-developer-pro" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-linkedin-in"></i>
              </a>
              <a href="https://wa.me/918826360188" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-whatsapp"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <motion.header
        className={`header ${isScrolled ? 'header--scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="header__container">
        {/* Logo */}
        <Link to="/" className="header__logo" onClick={closeMenu}>
          <motion.div 
            className="header__logo-wrapper"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          >
            <img 
              src="/images/app-developer-pro-e-logo.png" 
              alt="App Developer Pro Logo" 
              className="header__logo-image"
            />
            <motion.div 
              className="header__logo-glow"
              animate={{ 
                opacity: [0.4, 0.8, 0.4],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="header__nav">
          <ul className="header__nav-list">
            {navigation.map((item, index) => (
              <motion.li 
                key={item.name} 
                className={`header__nav-item ${item.hasDropdown ? 'header__nav-item--dropdown' : ''}`}
                ref={item.name === 'Services' ? dropdownRef : item.name === 'About' ? aboutDropdownRef : null}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                {item.hasDropdown ? (
                  <>
                    <button
                      className={`header__nav-link header__nav-dropdown-toggle ${
                        (item.name === 'Services' && location.pathname.startsWith('/services')) ||
                        (item.name === 'About' && (location.pathname.startsWith('/about') || location.pathname === '/full-stack-expert' || location.pathname === '/development-reviews' || location.pathname === '/portfolio'))
                        ? 'header__nav-link--active' : ''
                      }`}
                      onClick={item.name === 'Services' ? toggleServicesDropdown : toggleAboutDropdown}
                    >
                      <span className="header__nav-link-text">{item.name}</span>
                      <FaChevronDown className={`header__nav-dropdown-icon ${
                        (item.name === 'Services' && isServicesDropdownOpen) || 
                        (item.name === 'About' && isAboutDropdownOpen) 
                        ? 'header__nav-dropdown-icon--open' : ''
                      }`} />
                    </button>
                    <div 
                      className={`header__nav-dropdown ${
                        (item.name === 'Services' && isServicesDropdownOpen) || 
                        (item.name === 'About' && isAboutDropdownOpen) 
                        ? 'header__nav-dropdown--open' : ''
                      }`}
                    >
                      <div className="header__nav-dropdown-grid">
                        {item.dropdownItems?.map((dropdownItem, dropdownIndex) => {
                          const IconComponent = dropdownItem.icon;
                          return (
                            <motion.div
                              key={dropdownItem.name}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ 
                                opacity: (item.name === 'Services' && isServicesDropdownOpen) || 
                                        (item.name === 'About' && isAboutDropdownOpen) ? 1 : 0, 
                                y: (item.name === 'Services' && isServicesDropdownOpen) || 
                                   (item.name === 'About' && isAboutDropdownOpen) ? 0 : 10 
                              }}
                              transition={{ duration: 0.2, delay: dropdownIndex * 0.05 }}
                            >
                              <Link
                                to={dropdownItem.path}
                                className={`header__nav-dropdown-link ${
                                  location.pathname === dropdownItem.path ? 'header__nav-dropdown-link--active' : ''
                                }`}
                                onClick={closeMenu}
                              >
                                <div className="header__nav-dropdown-link-icon-wrapper">
                                  <IconComponent className="header__nav-dropdown-link-icon" />
                                </div>
                                <div className="header__nav-dropdown-link-content">
                                  <span className="header__nav-dropdown-link-title">{dropdownItem.name}</span>
                                  <span className="header__nav-dropdown-link-desc">Professional {dropdownItem.name.toLowerCase()}</span>
                                </div>
                              </Link>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`header__nav-link ${
                      location.pathname === item.path ? 'header__nav-link--active' : ''
                    } ${item.name === 'Contact' ? 'header__nav-link--cta' : ''}`}
                  >
                    <span className="header__nav-link-text">{item.name === 'Contact' ? "LET'S TALK" : item.name}</span>
                  </Link>
                )}
              </motion.li>
            ))}
          </ul>
        </nav>

        {/* Mobile Menu Toggle - Hamburger Style */}
        <motion.button
          className={`header__toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span></span>
          <span></span>
          <span></span>
        </motion.button>
      </div>

      {/* Enhanced Mobile Menu */}
      <div
        className={`header__mobile-menu ${isMenuOpen ? 'header__mobile-menu--open' : ''}`}
      >
        <div className="header__mobile-menu-overlay">
          {/* Close Button */}
          <button className="header__mobile-menu-close" onClick={closeMenu} aria-label="Close menu">
            <FaTimes />
          </button>
          
          <nav className="header__mobile-nav">
            <ul className="header__mobile-nav-list">
              {navigation.map((item, index) => (
                <li
                  key={item.name}
                  className={`header__mobile-nav-item ${item.hasDropdown ? 'header__mobile-nav-item--dropdown' : ''}`}
                  ref={item.name === 'Services' ? mobileDropdownRef : item.name === 'About' ? mobileAboutDropdownRef : null}
                >
                  {item.hasDropdown ? (
                    <>
                      <button
                        className={`header__mobile-nav-link header__mobile-nav-dropdown-toggle ${
                          (item.name === 'Services' && location.pathname.startsWith('/services')) ||
                          (item.name === 'About' && (location.pathname.startsWith('/about') || location.pathname === '/full-stack-expert' || location.pathname === '/development-reviews' || location.pathname === '/portfolio'))
                          ? 'header__mobile-nav-link--active' : ''
                        }`}
                        onClick={item.name === 'Services' ? toggleMobileServicesDropdown : toggleMobileAboutDropdown}
                        style={{
                          background: 'linear-gradient(135deg, #f0f4ff 0%, #e6ecff 100%)',
                          border: '2px solid #d1ddff',
                          fontSize: '1.1rem',
                          fontWeight: '500',
                          padding: '12px 14px'
                        }}
                      >
                        <span className="header__mobile-nav-link-text">{item.name}</span>
                        <FaChevronDown className={`header__mobile-nav-dropdown-icon ${
                          (item.name === 'Services' && isMobileServicesDropdownOpen) || 
                          (item.name === 'About' && isMobileAboutDropdownOpen) 
                          ? 'header__mobile-nav-dropdown-icon--open' : ''
                        }`} />
                      </button>
                      <div 
                        className={`header__mobile-nav-dropdown ${
                          (item.name === 'Services' && isMobileServicesDropdownOpen) || 
                          (item.name === 'About' && isMobileAboutDropdownOpen) 
                          ? 'header__mobile-nav-dropdown--open' : ''
                        }`}
                        style={{
                          maxHeight: (item.name === 'Services' && isMobileServicesDropdownOpen) || 
                                    (item.name === 'About' && isMobileAboutDropdownOpen) 
                                    ? '800px' : '0',
                          opacity: (item.name === 'Services' && isMobileServicesDropdownOpen) || 
                                  (item.name === 'About' && isMobileAboutDropdownOpen) 
                                  ? 1 : 0,
                          overflow: 'hidden',
                          transition: 'all 0.4s ease-out'
                        }}
                      >
                        {item.dropdownItems?.map((dropdownItem, dropdownIndex) => {
                          const IconComponent = dropdownItem.icon;
                          return (
                            <div key={dropdownItem.name}>
                              <Link
                                to={dropdownItem.path}
                                className={`header__mobile-nav-dropdown-link ${
                                  location.pathname === dropdownItem.path ? 'header__mobile-nav-dropdown-link--active' : ''
                                }`}
                                onClick={closeMenu}
                              >
                                <div className="header__mobile-nav-dropdown-link-icon-wrapper">
                                  <IconComponent className="header__mobile-nav-dropdown-link-icon" />
                                </div>
                                <span className="header__mobile-nav-dropdown-link-text">{dropdownItem.name}</span>
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`header__mobile-nav-link ${
                        location.pathname === item.path ? 'header__mobile-nav-link--active' : ''
                      }`}
                      onClick={closeMenu}
                    >
                      <span className="header__mobile-nav-link-text">{item.name}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            
            {/* Contact Information */}
            <div className="header__mobile-contact">
              <div className="header__mobile-contact-title">Contact Us</div>
              
              <a 
                href="tel:+918826360188" 
                className="header__mobile-contact-item"
              >
                <FaPhone className="header__mobile-contact-icon" />
                <span className="header__mobile-contact-value">+91 882 636 0188</span>
              </a>
                           
              <a 
                href="mailto:hello@appdeveloperpro.com" 
                className="header__mobile-contact-item"
              >
                <FaEnvelope className="header__mobile-contact-icon" />
                <span className="header__mobile-contact-value">hello@appdeveloperpro.com</span>
              </a>
            </div>
          </nav>
        </div>
      </div>
      </motion.header>
    </>
  );
};

export default Header;
